import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type DatabaseChangePreview } from '../../api';
import { useResource } from '../../hooks';
import { plural } from '../../labels';
import { ErrorNotice, LoadingRows, StatusBadge } from '../ui';
import { DatabaseSchemaChanges } from '../DatabaseSchemaChanges';
import { DatabaseCodeView, codeSummary } from '../DatabaseCodeChanges';
import { QuietEmpty } from './ui';
import { Callout, Field, Input } from '../primitives';
import { relativeTime } from './projectHealth';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { Initiator, WhenCell } from '../status/StatusPieces';

/**
 * Database change previews: the exact code a commit changes in the
 * database, read against the live Magento release before it is deployed.
 * The server checks the commit out beside the releases, installs its
 * dependencies (so vendor modules count), compares the files and removes
 * the checkout again. Nothing is deployed and the database is not touched.
 */

const hexCommit = /^[0-9a-f]{7,40}$/i;

/** What a preview found, in a few words, for the list. */
function previewOutcome(p: DatabaseChangePreview): string {
  if (!p.finished) return p.current_step ? `${p.current_step}…` : 'Starting…';
  if (p.status === 'cancelled') return 'Cancelled';
  const code = p.code;
  if (!code || code.state === 'not_run') return p.status === 'failed' ? 'Stopped before the comparison' : 'Not compared';
  if (code.state === 'unreadable' || code.state === 'not_available') return 'Could not be compared';
  if (code.file_count === 0) return 'No database changes';
  return `${plural(code.file_count, 'file')}: ${codeSummary(code)}`;
}

const previewColumns: Column<DatabaseChangePreview>[] = [
  {
    id: 'commit',
    header: 'Commit',
    card: 'title',
    width: 'code',
    cell: (p) => (
      <span className="cell-stack">
        <code translate="no">{p.commit_sha.slice(0, 10)}</code>
        {p.commit_ref ? <span className="cell-stack__secondary">{p.commit_ref}</span> : null}
      </span>
    ),
  },
  {
    id: 'outcome',
    header: 'What it found',
    width: 'fill',
    cell: (p) => (
      <span className="cell-stack">
        {p.finished && p.status !== 'completed' ? <StatusBadge kind="execution" value={p.status} /> : null}
        <span>{previewOutcome(p)}</span>
      </span>
    ),
  },
  {
    id: 'live',
    header: 'Compared with',
    priority: 'secondary',
    width: 24,
    cell: (p) => (p.live_release_tag ? <code className="db-previews__tag" translate="no">{p.live_release_tag}</code> : <span className="muted">Nothing live</span>),
  },
  {
    id: 'when',
    header: 'Started',
    width: 'time',
    cell: (p) => <WhenCell created={p.created_at} running={!p.finished} />,
  },
  { id: 'by', header: 'Started by', priority: 'secondary', card: 'none', width: 14, cell: (p) => <Initiator value={p.created_by} /> },
];

export function DatabasePreviewsPanel({
  projectId,
  environmentId,
  magentoId,
  defaultBranch,
  previewPath,
}: {
  projectId: string;
  environmentId: string;
  magentoId: string;
  defaultBranch: string;
  previewPath: (id: string) => string;
}) {
  const fieldId = useId();
  const [ref, setRef] = useState(defaultBranch);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [started, setStarted] = useState<DatabaseChangePreview | null>(null);
  const previews = useResource(() => api.listDatabaseChangePreviews(environmentId), [environmentId], environmentId, { key: `env:${environmentId}:database-previews` });
  const list = previews.data?.previews ?? [];
  const running = list.some((p) => !p.finished);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(previews.reload, 4000);
    return () => window.clearInterval(timer);
  }, [running, previews.reload]);

  async function start(e: React.FormEvent) {
    e.preventDefault();
    const value = ref.trim();
    if (!value) {
      setError(new Error('Enter a branch or a commit to preview.'));
      return;
    }
    setStarting(true);
    setError(null);
    try {
      let sha = value;
      let branch = '';
      if (!hexCommit.test(value)) {
        branch = value;
        sha = (await api.resolveApplicationBranch(projectId, magentoId, value)).commit_sha;
      }
      const preview = await api.previewDatabaseChanges(environmentId, sha, branch);
      setStarted(preview);
      previews.reload();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="db-previews" data-testid="database-previews">
      <form className="db-previews__form" onSubmit={start} noValidate>
        <Field label="Branch or commit" hint="A branch is previewed at its latest commit.">
          <Input
            id={fieldId}
            name="ref"
            type="text"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="main"
            className="input--mono"
            spellCheck={false}
            autoComplete="off"
          />
        </Field>
        {/* Starting a new preview; opening an existing one is "View changes" in the list. */}
        <button type="submit" className="button button--secondary" disabled={starting} data-testid="preview-database-changes">
          {starting ? 'Starting preview' : 'Start a preview'}
        </button>
      </form>
      <ErrorNotice error={error} />
      {started ? (
        <p className="panel__note" role="status">
          Preview of <code translate="no">{started.commit_sha.slice(0, 10)}</code> started.{' '}
          <Link to={previewPath(started.id)}>Follow it</Link>
        </p>
      ) : null}

      {previews.loading && list.length === 0 ? (
        <LoadingRows label="Loading previews" rows={3} rowHeight="40px" />
      ) : previews.error && list.length === 0 ? (
        <ErrorNotice error={previews.error} />
      ) : list.length === 0 ? (
        <QuietEmpty testId="no-previews">No previews</QuietEmpty>
      ) : (
        <ResponsiveTable
          rows={list}
          columns={previewColumns}
          rowKey={(p) => p.id}
          caption="Database change previews, newest first"
          testId="database-previews-table"
          rowLink={(p) => ({ to: previewPath(p.id), accessibleName: `View the database changes of ${p.commit_sha.slice(0, 10)}` })}
        />
      )}
    </div>
  );
}

/** DatabasePreviewView is one preview: its progress, then what it found. */
export function DatabasePreviewView({ previewId, runPath, onBack }: { previewId: string; runPath: (executionId: string) => string; onBack: () => void }) {
  const preview = useResource(() => api.getDatabaseChangePreview(previewId), [previewId], previewId);
  const p = preview.data;
  const finished = p?.finished ?? false;

  useEffect(() => {
    if (!p || finished) return;
    const timer = window.setInterval(preview.reload, 3000);
    return () => window.clearInterval(timer);
  }, [p, finished, preview.reload]);

  return (
    <section className="db-preview panel" aria-labelledby="db-preview-title" data-testid="database-preview">
      <button type="button" className="button button--ghost button--small db-preview__back" onClick={onBack}>
        All previews
      </button>
      {!p ? (
        preview.error ? (
          <ErrorNotice error={preview.error} />
        ) : (
          <LoadingRows label="Loading the preview" rows={4} rowHeight="44px" />
        )
      ) : (
        <>
          <div className="db-preview__head">
            <h2 id="db-preview-title" className="db-preview__title">
              Database changes in <code translate="no">{p.commit_sha.slice(0, 10)}</code>
            </h2>
            <StatusBadge kind="execution" value={p.status} />
          </div>
          <p className="type-meta db-preview__meta">
            {p.commit_ref ? (
              <>
                Branch <code translate="no">{p.commit_ref}</code> ·{' '}
              </>
            ) : null}
            {p.live_release_tag ? (
              <>
                Live release <code translate="no">{p.live_release_tag}</code>
              </>
            ) : (
              'No live release'
            )}{' '}
            · by {p.created_by} · {relativeTime(p.created_at)}
          </p>

          {!finished ? (
            <p className="panel__note" role="status" data-testid="database-preview-progress">
              {p.current_step ? `${p.current_step}.` : 'Starting.'} <Link to={runPath(p.execution_id)}>View the log</Link>
            </p>
          ) : p.status !== 'completed' ? (
            <Callout tone="danger" title={p.status === 'cancelled' ? 'The preview was cancelled' : 'The preview stopped before it finished'}>
              {p.error_message ? <p>{p.error_message}</p> : null}
              <p>
                <Link to={runPath(p.execution_id)}>View the log</Link> · the preview’s checkout was removed
              </p>
            </Callout>
          ) : null}

          {p.schema && p.schema.state !== 'pending' ? <DatabaseSchemaChanges schema={p.schema} /> : null}
          {p.code && p.code.state !== 'not_run' ? <DatabaseCodeView code={p.code} /> : null}
          {finished && p.status === 'completed' ? (
            <p className="panel__note muted">
              Not known until deployed: what data patches write, and Magento’s own status check.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
